/**
 * 身份与时间。品牌类型保证 ConceptId 与 QuestionId 不能互换传入某个函数，
 * EpochDay 与 IntervalDays 这两个 number 不会互相顶替。
 * 除本文件外没有任何地方能构造这些值。所有层都依赖它，它不 import 任何其他核心模块。
 * 不做诊断。非法字符串返回 null，把它翻译成 Issue 是 parsing 与 bridge 两个边界的事。
 */

declare const brandTag: unique symbol

export type Branded<T, Tag extends string> = T & { readonly [brandTag]: Tag }

/** 条目身份，来自 frontmatter 的 id，仓库内唯一。文件路径变化不影响它。 */
export type ConceptId = Branded<string, 'ConceptId'>

/** 题目身份，形如 `${conceptId}/${localId}`，只能由 questionId 构造。 */
export type QuestionId = Branded<string, 'QuestionId'>

/** 仓库内相对路径，斜杠分隔，只用于展示与错误定位，不参与身份。 */
export type RepoPath = Branded<string, 'RepoPath'>

/** 一次提交的幂等键，形如 `${planId}#${cursor}`，日志折叠时按它去重。 */
export type ReviewId = Branded<string, 'ReviewId'>

/** 一次排期的身份。同一 intent 在同一天重建得到同一个值，所以重放不会多写一条历史。 */
export type PlanId = Branded<string, 'PlanId'>

/** 设备标识，二期多端合并日志时用来区分写入者。 */
export type DeviceId = Branded<string, 'DeviceId'>

/** 本地日历日，1970-01-01 起的天数。到期比较只用它，不用时间戳，跨时区和夏令时都不会让一天变成两天。 */
export type EpochDay = Branded<number, 'EpochDay'>

/** 复习间隔，单位天。与 EpochDay 分开，避免两个数字语义混用。 */
export type IntervalDays = Branded<number, 'IntervalDays'>

/** 可复习单位。题目卡和知识点卡是两种卡，共用同一套调度与同一份日志。 */
export type CardRef =
	| { readonly kind: 'concept'; readonly conceptId: ConceptId }
	| { readonly kind: 'question'; readonly questionId: QuestionId }

/** CardRef 的序列化形式，日志与状态表的键。前缀区分两种卡。 */
export type CardKey = Branded<string, 'CardKey'>

export const CONCEPT_CARD_PREFIX = 'c:'
export const QUESTION_CARD_PREFIX = 'q:'

/**
 * 派生知识点卡占用 `recall` 这个锚点（含 `recall:` 前缀写法）。
 * 作者题块用了它会让两张卡指向同一个名字，所以解析层先拒保留字，再拒非法字符集。
 */
export const RECALL_LOCAL_ID = 'recall'

/** 身份字符集：小写字母或数字开头，其后允许连字符。concept 与 local 共用。 */
const ID_CHARSET = /^[a-z0-9][a-z0-9-]*$/

/** 字符集 `[a-z0-9][a-z0-9-]*`。不合法返回 null，由调用方转成 Issue。 */
export function parseConceptId(raw: unknown): ConceptId | null {
	return typeof raw === 'string' && ID_CHARSET.test(raw) ? (raw as ConceptId) : null
}

/** 题块 `id` 的本地部分。保留字与非法字符集都返回 null。 */
export function parseLocalQuestionId(raw: unknown): string | null {
	if (typeof raw !== 'string' || isReservedLocalId(raw)) return null
	return ID_CHARSET.test(raw) ? raw : null
}

/** 先于字符集判断，让保留字给出专门的 reserved-id 诊断而不是笼统的 bad-value。 */
export function isReservedLocalId(raw: string): boolean {
	return raw === RECALL_LOCAL_ID || raw.startsWith(`${RECALL_LOCAL_ID}:`)
}

export function toRepoPath(raw: string): RepoPath {
	return raw as RepoPath
}

/** `${concept}/${local}`。concept id 的字符集已过滤，拼接不会产生歧义分隔符。 */
export function questionId(concept: ConceptId, local: string): QuestionId {
	return `${concept}/${local}` as QuestionId
}

export function conceptOfQuestion(id: QuestionId): ConceptId {
	return id.slice(0, id.indexOf('/')) as ConceptId
}

export function localOfQuestion(id: QuestionId): string {
	return id.slice(id.indexOf('/') + 1)
}

export function conceptCard(conceptId: ConceptId): CardRef {
	return { kind: 'concept', conceptId }
}

export function questionCard(id: QuestionId): CardRef {
	return { kind: 'question', questionId: id }
}

/** 'c:' + conceptId 或 'q:' + questionId。日志键与状态表键只有这一种构造方式。 */
export function cardKey(ref: CardRef): CardKey {
	const raw = ref.kind === 'concept' ? `${CONCEPT_CARD_PREFIX}${ref.conceptId}` : `${QUESTION_CARD_PREFIX}${ref.questionId}`
	return raw as CardKey
}

/** 日志行的键字段回到 CardRef。坏数据返回 null，由日志边界丢弃并记 Issue。 */
export function parseCardKey(raw: string): CardRef | null {
	if (raw.startsWith(CONCEPT_CARD_PREFIX)) {
		const conceptId = parseConceptId(raw.slice(CONCEPT_CARD_PREFIX.length))
		return conceptId === null ? null : { kind: 'concept', conceptId }
	}
	if (raw.startsWith(QUESTION_CARD_PREFIX)) {
		const id = raw.slice(QUESTION_CARD_PREFIX.length)
		// local 与 concept 的字符集都不含斜杠，一个斜杠就是唯一分界。
		return id.includes('/') ? { kind: 'question', questionId: id as QuestionId } : null
	}
	return null
}

/** 两种卡都落在同一个条目上，知识点卡和它下面的题卡共享条目级查询。 */
export function cardConceptId(ref: CardRef): ConceptId {
	return ref.kind === 'concept' ? ref.conceptId : conceptOfQuestion(ref.questionId)
}

/** 排期身份由 intent 的稳定序列化、当天、卡片键集合算出后作为 seed 传入，这里只做品牌化。 */
export function planId(seed: string): PlanId {
	return seed as PlanId
}

/** 提交幂等键的唯一构造点。cursor 把它绑到「本轮第几次作答」，重放同一次作答得到同一个键。 */
export function submissionId(plan: PlanId, cursor: number): ReviewId {
	return `${plan}#${cursor}` as ReviewId
}

export function reviewId(raw: string): ReviewId {
	return raw as ReviewId
}

export function deviceId(raw: string): DeviceId {
	return raw as DeviceId
}

/** 本地 Date → 本地日历日。用本地年月日构造 UTC 零点再取天数，时区与夏令时都不会让「今天」漂移。 */
export function epochDayOf(local: Date): EpochDay {
	const days = Date.UTC(local.getFullYear(), local.getMonth(), local.getDate()) / 86_400_000
	return toEpochDay(days)
}

export function toEpochDay(days: number): EpochDay {
	return days as EpochDay
}

export function addDays(day: EpochDay, days: IntervalDays): EpochDay {
	return (day + days) as EpochDay
}

export function daysBetween(from: EpochDay, to: EpochDay): number {
	return to - from
}

/** 内部算术结果在这里不做校验，IntervalDays 的合法区间由 scheduling.advance 的钳制保证。 */
export function interval(days: number): IntervalDays {
	return days as IntervalDays
}
